import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../../React/Common/Contexts'
import { ChangeUserAgentLanguage } from '../../../../../../i18n'
import { OutlinedSelect } from '../../../Atoms/Inputs/OutlinedSelect'
import { SelectOptions } from '../../../../../React/Common/Components/Selects'
import { isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import {
    getAvailableUserAgents,
    getUserAgentFromSession,
    setUserAgent
} from '../../../../../React/Common/API/Session/UserAgentAPI'
import { UserAgentDTO } from '../../../../../React/Common/Types/Types'
import { RoundedButton } from '../../../Atoms/RoundedButton/RoundedButton'
import { AUTH_ROUTE_PATH } from '../../../../Constants/Constants'
import { displayError } from '../../../../../React/Common/Utils/Messages'

const DEFAULT_USER_AGENT: number = -1

export const ChangeUserAgent: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(ChangeUserAgentLanguage.NAME)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [availableUserAgents, setAvailableUserAgents] = useState<Nullable<SelectOptions<number>>>([
        {
            label: language.get(ChangeUserAgentLanguage.ChangeUserAgent_Options_ForceAutomaticBrowserDetection),
            value: DEFAULT_USER_AGENT
        }
    ])
    const [currentUserAgent, setCurrentUserAgent] = useState<Nullable<number>>(DEFAULT_USER_AGENT)

    const location: Location = useLocation()
    const navigate: NavigateFunction = useNavigate()

    useEffect((): void => {
        getAvailableUserAgents()
            .then((userAgents: Nullable<SelectOptions<number>>): void => {
                if (isMounted.current) {
                    setAvailableUserAgents([
                        {
                            label: language.get(
                                ChangeUserAgentLanguage.ChangeUserAgent_Options_ForceAutomaticBrowserDetection
                            ),
                            value: DEFAULT_USER_AGENT
                        },
                        ...(userAgents ?? [])
                    ])
                }
            })
            .catch(displayError)

        getUserAgentFromSession()
            .then((userAgent: Nullable<UserAgentDTO>): void => {
                if (isMounted.current) setCurrentUserAgent(userAgent?.userAgentID ?? DEFAULT_USER_AGENT)
            })
            .catch(displayError)
    }, [])

    return (
        <div className='change-user-agent-content'>
            <h1 className='cua-title'>{language.get(ChangeUserAgentLanguage.ChangeUserAgent_Title)}</h1>
            <h2 className='cua-subtitle'>{language.get(ChangeUserAgentLanguage.ChangeUserAgent_SubTitle)}</h2>

            {isVarNotEmpty(availableUserAgents) ? (
                <OutlinedSelect
                    containerClassName='cua-select'
                    options={availableUserAgents}
                    label={language.get(ChangeUserAgentLanguage.ChangeUserAgent_FieldName_BrowserProfile)}
                    value={currentUserAgent ?? DEFAULT_USER_AGENT}
                    handleChange={setCurrentUserAgent}
                />
            ) : (
                <></>
            )}

            <div className='cua-footer-actions'>
                <RoundedButton
                    type='button'
                    onClick={(): void => navigate(AUTH_ROUTE_PATH, { state: { from: location } })}
                >
                    {language.get(ChangeUserAgentLanguage.ChangeUserAgent_FooterActions_Cancel)}
                </RoundedButton>
                <RoundedButton
                    type='button'
                    onClick={(): void => {
                        setUserAgent(currentUserAgent ?? DEFAULT_USER_AGENT)
                            .then((): void => navigate(AUTH_ROUTE_PATH, { state: { from: location } }))
                            .catch(displayError)
                    }}
                >
                    {language.get(ChangeUserAgentLanguage.ChangeUserAgent_FooterActions_Apply)}
                </RoundedButton>
            </div>
        </div>
    )
}
